import axios from "axios";
import { Modal } from "antd";

import { API_BASE_PATH, CHECKLIST_API_BASE_URL } from "../config";

const ADMIN_TOKEN_KEY = "token";
// Left behind by the old separate Operations login; cleared on start-up.
const LEGACY_OPERATIONS_TOKEN_KEY = "checklist_token";
const SESSION_KEYS = [ADMIN_TOKEN_KEY, "user_data", "user_role_id", "storeId"];
const RETURN_RECORD_KEY = "session_ended_on";

// Login and password-reset calls run without a session, so their failures
// never mean "expired".
const EXEMPT_MAIN_PATHS = ["mobile/loginUser", "mobile/reset-password"];
const MAIN_EXPIRED_MESSAGES = ["Invalid token.", "Token does not exist"];

let sessionEnded = false;
let signedInHere = false;

function readJsonKey(key) {
  const value = localStorage.getItem(key);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (e) {
    // A corrupt value would otherwise crash every JSON.parse of it.
    localStorage.removeItem(key);
    return null;
  }
}

export function getAdminToken() {
  return readJsonKey(ADMIN_TOKEN_KEY);
}

export function isSessionEnded() {
  return sessionEnded;
}

export function setSignedInHere(value) {
  signedInHere = value;
}

// Returns (and forgets) where this tab was when its session ended, so the
// next login can decide whether to send the user back there.
export function takeSessionEndRecord() {
  try {
    const record = JSON.parse(sessionStorage.getItem(RETURN_RECORD_KEY));
    sessionStorage.removeItem(RETURN_RECORD_KEY);
    return record;
  } catch (e) {
    return null;
  }
}

// `expired`: this tab's login stopped working, so clear it.
// `other-tab`: another tab logged out or switched accounts; that tab owns the
// stored login now, so only this tab's screen needs resetting.
function endSession(reason) {
  if (sessionEnded) return;
  sessionEnded = true;

  try {
    sessionStorage.setItem(
      RETURN_RECORD_KEY,
      JSON.stringify({ userId: readJsonKey("user_data")?.id ?? null })
    );
  } catch (e) {
    // sessionStorage unavailable; the next login just won't compare users.
  }

  if (reason === "expired") {
    SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  }

  const expired = reason === "expired";
  // No mask, so anything half-typed on the page can still be copied first.
  Modal.warning({
    title: expired ? "Your session has expired" : "Your login changed in another tab",
    content: expired
      ? "Please log in again to continue. Anything on this page that wasn't saved couldn't be saved, so copy it first if you need it."
      : "Reload to continue with the current login.",
    okText: expired ? "Log in again" : "Reload",
    mask: false,
    keyboard: false,
    onOk: () => window.location.reload(),
  });
}

function getRequestToken(config) {
  const headers = config?.headers;
  const header = headers?.get ? headers.get("Authorization") : headers?.Authorization;
  return typeof header === "string" ? header.replace(/^Bearer\s+/, "") : null;
}

function checkForExpiredSession(config, status, body) {
  const url = config?.url || "";
  const isMain = url.startsWith(API_BASE_PATH);
  // Match the path too: the staff API is served from the same host under /rapsap-staff.
  const isOperations =
    Boolean(CHECKLIST_API_BASE_URL) && url.startsWith(`${CHECKLIST_API_BASE_URL}/rapsap-operations/`);
  if (!isMain && !isOperations) return;
  if (isMain && EXEMPT_MAIN_PATHS.some((path) => url.includes(path))) return;

  // Main API: only its known login-failure messages count (it has sent them
  // as both 401 and 200), so a 401 meaning "not allowed" doesn't log anyone
  // out. Operations API: any 401, as its login-failure messages aren't known.
  const expired = isMain ? MAIN_EXPIRED_MESSAGES.includes(body?.msg) : status === 401;
  if (!expired) return;

  // Both APIs take the one login token. Only end the session if the request
  // carried the token that's still stored: a slow answer to a request sent
  // before a re-login must not log the new session out.
  const sentToken = getRequestToken(config);
  const sessionToken = getAdminToken();
  if (sessionToken && sentToken === String(sessionToken)) endSession("expired");
}

function handleStorageChange(event) {
  if (![ADMIN_TOKEN_KEY, "user_data", null].includes(event.key)) return;

  if (!signedInHere) {
    // Another tab logged in while this one shows the login screen.
    if (getAdminToken()) window.location.reload();
    return;
  }

  // key === null means another tab cleared all of storage at once.
  const tokenRemoved =
    event.key === null
      ? !getAdminToken()
      : event.key !== "user_data" && event.oldValue !== null && event.newValue === null;
  let userChanged = false;
  if (event.key === "user_data" && event.oldValue && event.newValue) {
    try {
      userChanged = JSON.parse(event.oldValue)?.id !== JSON.parse(event.newValue)?.id;
    } catch (e) {
      userChanged = true;
    }
  }
  if (tokenRemoved || userChanged) endSession("other-tab");
}

export function installSessionHandling() {
  // A browser still holding an old Operations login has no main token, so it
  // simply lands on the login page; drop the stale key.
  localStorage.removeItem(LEGACY_OPERATIONS_TOKEN_KEY);
  axios.interceptors.response.use(
    (response) => {
      checkForExpiredSession(response.config, response.status, response.data);
      return response;
    },
    (error) => {
      if (error?.response) {
        checkForExpiredSession(error.config, error.response.status, error.response.data);
      }
      return Promise.reject(error);
    }
  );
  window.addEventListener("storage", handleStorageChange);
}
